import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const PREFIX = 'url-hashtag:';

/**
 * The `?hashtag=` deep link on the Explore hashtag tables: the URL names an
 * open drawer, and opening or closing one rewrites the URL.
 *
 * The selected id is prefixed so the table can tell a link-opened drawer from
 * a clicked row - the linked hashtag may not be in the fetched rows at all.
 *
 * `drawerProps` is empty when disabled rather than carrying undefined values,
 * because DataTable treats a defined `controlled*` prop as taking over its own
 * drawer state.
 */
const useHashtagDeepLink = ({ enabled, rows }) => {
    const location = useLocation();
    const history = useHistory();

    const hashtag = enabled
        ? new URLSearchParams(location.search).get('hashtag')
        : null;

    const [open, setOpen] = useState(!!hashtag);
    const [selectedId, setSelectedId] = useState(
        hashtag ? `${PREFIX}${hashtag}` : null,
    );

    useEffect(() => {
        if (enabled && hashtag) {
            setSelectedId(`${PREFIX}${hashtag}`);
            setOpen(true);
        } else {
            setSelectedId(null);
            setOpen(false);
        }
    }, [hashtag, enabled]);

    const onDrawerChange = useCallback(
        (isOpen, id) => {
            if (!enabled) return;
            const params = new URLSearchParams(location.search);
            if (isOpen && id && !id.startsWith(PREFIX)) {
                const row = rows?.find(({ id: rowId }) => rowId === id);
                if (!row) return;
                params.set('hashtag', row.name.replace('#', ''));
                history.push({ search: params.toString() });
            } else if (!isOpen && params.has('hashtag')) {
                params.delete('hashtag');
                history.push({ search: params.toString() });
            }
        },
        [enabled, rows, location.search, history],
    );

    return {
        drawerProps: enabled
            ? {
                  controlledSelectedId: selectedId,
                  controlledDrawerOpen: open,
                  onDrawerChange,
              }
            : {},
        /** The '#'-prefixed name a linked id refers to, or null for a row id. */
        linkedHashtag: (id) =>
            enabled && id?.startsWith(PREFIX)
                ? `#${id.replace(PREFIX, '')}`
                : null,
    };
};

export default useHashtagDeepLink;
